import { useState } from "react";
import { useDispatch } from "react-redux";

import { Specimen } from "pwapi";

import { deleteSpecimen } from "shared/actions/admin/adminSpecimenActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import {
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_MAILTO,
} from "shared/utils/siteConstants";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";

import PopUpDialog from "../PopUpDialog";

type SpecimenWarningDialogProps = {
  adminCase: CaseAdminDetail;
  specimen: Specimen;
  onClose: Function;
  onClosePanel?: Function;
};

const SpecimenWarningDialog = ({
  adminCase,
  specimen,
  onClose,
  onClosePanel,
}: SpecimenWarningDialogProps) => {
  const dispatch = useDispatch();

  const isDeleting = useIsLoading(`admin/specimens/${specimen.id}/delete`);
  const [hasDeleteError, setHasDeleteError] = useState<boolean>(false);

  const slidesInSpecimen = adminCase.slides.filter(
    (slide) => slide.specimen_id === specimen?.id
  );
  const blocksInSpecimen = adminCase.blocks?.filter(
    (block) => block.specimen_id === specimen?.id
  );

  const onDeleteSpecimen = (specimen: Specimen) => {
    dispatch(
      deleteSpecimen({
        id: specimen.id!,
        case_id: adminCase.id,
      })
    )
      .then(() => {
        onClosePanel && onClosePanel();
        onClose();
      })
      .catch(() => {
        //TODO: Have the API send a proper message here, currently it is just a 500.
        setHasDeleteError(true);
      });
  };

  return (
    <PopUpDialog
      onClose={() => onClose()}
      dialogStatus={isDeleting ? "loading" : undefined}
      title={`Delete Specimen ${specimen.name}`}
      type="warning"
    >
      <div className="CaseWarningDialog">
        {!!blocksInSpecimen?.length || !!slidesInSpecimen.length ? (
          <>
            Specimen {specimen.name} cannot be deleted until all{" "}
            {[
              blocksInSpecimen?.length && "blocks",
              slidesInSpecimen?.length && "slides",
            ]
              .filter((s) => s)
              .join(" and ")}{" "}
            are removed from the specimen.
            <br />
            <br />
            {!!blocksInSpecimen?.length && (
              <>
                There{" "}
                {blocksInSpecimen.length === 1
                  ? "is 1 block"
                  : `are ${blocksInSpecimen.length} blocks`}{" "}
                still associated with this specimen.
                <br />
              </>
            )}
            {!!slidesInSpecimen?.length && (
              <>
                There{" "}
                {slidesInSpecimen.length === 1
                  ? "is 1 slide"
                  : `are ${slidesInSpecimen.length} slides`}{" "}
                still associated with this specimen.
                <br />
              </>
            )}
          </>
        ) : (
          <span>Would you like to delete this specimen?</span>
        )}
        {hasDeleteError && (
          <p className="errorMessage">
            Unexpected error occurred while attempting to delete this specimen.{" "}
            Please contact support to identify the issue.
            <br />
            <a href={SUPPORT_EMAIL_MAILTO}>{SUPPORT_EMAIL}</a>
          </p>
        )}
        <div className="actions__container">
          <PathologyWatchButton onClick={() => onClose()}>
            Cancel
          </PathologyWatchButton>
          <PathologyWatchButton
            type="submit"
            color="red"
            onClick={() => {
              onDeleteSpecimen(specimen);
            }}
            disabled={!!blocksInSpecimen?.length || !!slidesInSpecimen.length}
          >
            Delete
          </PathologyWatchButton>
        </div>
      </div>
    </PopUpDialog>
  );
};

export default SpecimenWarningDialog;
